'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

interface AiSectionProps {
  locale: string;
  labels: {
    eyebrow1: string;
    eyebrow2: string;
    description: string;
    placeholder: string;
    button: string;
    disclaimer: string;
    orb1Short: string;
    orb1Planet: string;
    orb1Value: string;
    orb1Label: string;
    orb2Short: string;
    orb2Planet: string;
    orb2Value: string;
    orb2Label: string;
    orb3Short: string;
    orb3Planet: string;
    orb3Value: string;
    orb3Label: string;
  };
}

const MAX_QUESTION_LENGTH = 500;

export function AiAstrologerSection({ locale, labels }: AiSectionProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim().slice(0, MAX_QUESTION_LENGTH);
    const basePath = locale === 'hi' ? '/hi/ai-astrologer' : '/ai-astrologer';
    if (trimmed) {
      router.push(`${basePath}?q=${encodeURIComponent(trimmed)}`);
    } else {
      router.push(basePath);
    }
  };

  return (
    <section id="ai" className="relative z-10 max-w-[1000px] mx-auto py-16 px-4">
      <div className="flex justify-between text-xs text-[rgba(220,236,255,0.7)] px-2 mb-8">
        <span>{labels.eyebrow1}</span>
        <span>{labels.eyebrow2}</span>
      </div>

      {/* Sample Badge */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase text-[#8dffc9] bg-white/5 border border-white/15 backdrop-blur-md">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8dffc9]" />
          <span>{locale === 'hi' ? 'नमूना जन्म कुंडली आंकड़े' : 'SAMPLE CHART METRICS'}</span>
        </div>
      </div>

      {/* 3 Metric Orbs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 justify-items-center mb-12">
        {/* Orb 1: Active Dasha */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full grid place-items-center font-heading font-semibold text-2xl text-white shadow-[0_0_50px_rgba(255,190,120,0.45),inset_-14px_-14px_30px_rgba(10,8,40,0.55)] bg-[radial-gradient(circle_at_35%_30%,#ffe9c9,#d9a066_45%,#7a3e2a_95%)] motion-safe:animate-[breathe_4s_ease-in-out_infinite]">
            {labels.orb1Short}
          </div>
          <span className="text-[11px] font-semibold tracking-widest text-[#b9a8ff] mt-2">
            {labels.orb1Planet}
          </span>
          <span className="font-heading font-semibold text-base text-white">
            {labels.orb1Value}
          </span>
          <span className="text-xs text-[rgba(220,236,255,0.75)]">
            {labels.orb1Label}
          </span>
        </div>

        {/* Orb 2: Lagna */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full grid place-items-center font-heading font-semibold text-xl sm:text-2xl text-white shadow-[0_0_50px_rgba(255,190,90,0.6),inset_-14px_-14px_30px_rgba(10,8,40,0.55)] bg-[radial-gradient(circle_at_40%_35%,#fffbe6,#ffd36b_40%,#ff8a2a_85%)] motion-safe:animate-[breathe_4s_ease-in-out_-1.5s_infinite]">
            {labels.orb2Short}
          </div>
          <span className="text-[11px] font-semibold tracking-widest text-[#b9a8ff] mt-2">
            {labels.orb2Planet}
          </span>
          <span className="font-heading font-semibold text-base text-white">
            {labels.orb2Value}
          </span>
          <span className="text-xs text-[rgba(220,236,255,0.75)]">
            {labels.orb2Label}
          </span>
        </div>

        {/* Orb 3: Sarvashtakavarga Bindus */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full grid place-items-center font-heading font-semibold text-2xl text-white shadow-[0_0_50px_rgba(150,165,255,0.55),inset_-14px_-14px_30px_rgba(10,8,40,0.55)] bg-[radial-gradient(circle_at_35%_30%,#eef1ff,#9aa6ff_45%,#3a2f9a_95%)] motion-safe:animate-[breathe_4s_ease-in-out_-3s_infinite]">
            {labels.orb3Short}
          </div>
          <span className="text-[11px] font-semibold tracking-widest text-[#b9a8ff] mt-2">
            {labels.orb3Planet}
          </span>
          <span className="font-heading font-semibold text-base text-white">
            {labels.orb3Value}
          </span>
          <span className="text-xs text-[rgba(220,236,255,0.75)]">
            {labels.orb3Label}
          </span>
        </div>
      </div>

      {/* Description + Input Form */}
      <div className="max-w-[660px] mx-auto text-center flex flex-col items-center gap-5">
        <p className="text-sm sm:text-base text-[rgba(225,238,255,0.85)] leading-relaxed">
          {labels.description}
        </p>

        <form
          onSubmit={handleSubmit}
          className="w-full flex flex-col sm:flex-row items-center gap-2.5 p-1.5 sm:pl-5 rounded-3xl sm:rounded-full bg-gradient-to-r from-white/16 to-white/[0.04] border border-white/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_0_50px_rgba(90,80,255,0.25)] backdrop-blur-xl"
        >
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={MAX_QUESTION_LENGTH}
            placeholder={labels.placeholder}
            className="w-full sm:flex-1 min-w-0 bg-transparent border-0 outline-none px-3 py-2 text-sm text-white placeholder-[rgba(200,225,255,0.6)] font-sans"
          />
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_20px_rgba(150,165,255,0.6)] transition-all cursor-pointer shrink-0"
          >
            {labels.button}
          </button>
        </form>

        <span className="text-xs text-[rgba(220,236,255,0.6)]">
          {labels.disclaimer}
        </span>
      </div>
    </section>
  );
}
